/**
 * RRA VSDC Specification §4.5 (Packaging Unit) and §4.6 (Unit of Quantity)
 * reference tables, restricted to what ebm-rw's buildItemCd()/parseItemCd()
 * can actually accept today: both hard-validate pkgUnitCd/qtyUnitCd against
 * /^[A-Z]{2}$/. RRA's own official tables include several 3-character codes
 * (CTN, BLL, GRM, LTR, ...), a couple of 1-character codes (L, M, U), and
 * even a few 2-character codes that mix a digit with a letter (4B, M2, M3)
 * — none of these are valid SDK input despite being real, common RRA units.
 * `selectable` below is computed from the actual regex, not character
 * count, so it correctly excludes 4B/M2/M3 too.
 *
 * RESOLVED (was previously an open risk): RRA's own official spec example
 * (§4.17, "RW2NTBA0000012") and a live /code/selectCodes call against the
 * RW test server (2026-08-19) both confirm packaging/quantity unit codes
 * are 2 uppercase letters only — the earlier concern was a misread of a
 * blurry taxpayer-manual screenshot. ebm-rw's regex was already correct;
 * no code change was needed.
 */

export interface EbmUnitCode {
  code: string;
  name: string;
  selectable: boolean;
}

const SDK_UNIT_CODE_PATTERN = /^[A-Z]{2}$/;

function unit(code: string, name: string): EbmUnitCode {
  return { code, name, selectable: SDK_UNIT_CODE_PATTERN.test(code) };
}

export const EBM_PACKAGING_UNIT_CODES: EbmUnitCode[] = [
  unit('AM', 'Ampoule'),
  unit('BA', 'Barrel'),
  unit('BC', 'Bottlecrate'),
  unit('BE', 'Bundle'),
  unit('BF', 'Balloon, non-protected'),
  unit('BG', 'Bag'),
  unit('BJ', 'Bucket'),
  unit('BK', 'Basket'),
  unit('BL', 'Bale'),
  unit('BQ', 'Bottle, protected cylindrical'),
  unit('BR', 'Bar'),
  unit('BV', 'Bottle, bulbous'),
  unit('BZ', 'Bag'),
  unit('CA', 'Can'),
  unit('CH', 'Chest'),
  unit('CJ', 'Coffin'),
  unit('CL', 'Coil'),
  unit('CR', 'Wooden Box, Wooden Case'),
  unit('CS', 'Cassette'),
  unit('CT', 'Carton'),
  unit('CTN', 'Container'),
  unit('CY', 'Cylinder'),
  unit('DR', 'Drum'),
  unit('GT', 'Extra Countable Item'),
  unit('HH', 'Hand Baggage'),
  unit('IZ', 'Ingots'),
  unit('JR', 'Jar'),
  unit('JU', 'Jug'),
  unit('JY', 'Jerry Can Cylindrical'),
  unit('KZ', 'Canester'),
  unit('LZ', 'Logs, in bundle/bunch/truss'),
  unit('NT', 'Net'),
  unit('OU', 'Non-Exterior Packaging Unit'),
  unit('PD', 'Poddon'),
  unit('PG', 'Plate'),
  unit('PI', 'Pipe'),
  unit('PO', 'Pilot'),
  unit('PU', 'Traypack'),
  unit('RL', 'Reel'),
  unit('RO', 'Roll'),
  unit('RZ', 'Rods, in bundle/bunch/truss'),
  unit('SK', 'Skeletoncase'),
  unit('TY', 'Tank, cylindrical'),
  unit('VG', 'Bulk, gas (at 1031 mbar 15°C)'),
  unit('VL', 'Bulk, liquid (at normal temp/pressure)'),
  unit('VO', 'Bulk, solid, large particles ("nodules")'),
  unit('VQ', 'Bulk, gas (liquefied at abnormal temp/pressure)'),
  unit('VR', 'Bulk, solid, granular particles ("grains")'),
  unit('VT', 'Extra Bulk Item'),
  unit('VY', 'Bulk, fine particles ("powder")'),
  unit('ML', 'Mills (cigarette)'),
  unit('TN', 'TAN (1 TAN = 20 bags)'),
];

export const EBM_QUANTITY_UNIT_CODES: EbmUnitCode[] = [
  unit('4B', 'Pair'),
  unit('AV', 'Cap'),
  unit('BA', 'Barrel'),
  unit('BE', 'Bundle'),
  unit('BG', 'Bag'),
  unit('BL', 'Block'),
  unit('BLL', 'Barrel (petroleum, 158.987 dm³)'),
  unit('BX', 'Box'),
  unit('CA', 'Can'),
  unit('CEL', 'Cell'),
  unit('CMT', 'Centimetre'),
  unit('CR', 'Carat'),
  unit('DR', 'Drum'),
  unit('DZ', 'Dozen'),
  unit('GLL', 'Gallon'),
  unit('GRM', 'Gram'),
  unit('GRO', 'Gross'),
  unit('KG', 'Kilo-Gramme'),
  unit('KTM', 'Kilometre'),
  unit('KWT', 'Kilowatt'),
  unit('L', 'Litre'),
  unit('LBR', 'Pound'),
  unit('LK', 'Link'),
  unit('LTR', 'Litre'),
  unit('M', 'Metre'),
  unit('M2', 'Square Metre'),
  unit('M3', 'Cubic Metre'),
  unit('MGM', 'Milligram'),
  unit('MTR', 'Metre'),
  unit('MWT', 'Megawatt hour (1000 kW·h)'),
  unit('NO', 'Number'),
  unit('NX', 'Part per thousand'),
  unit('PA', 'Packet'),
  unit('PG', 'Plate'),
  unit('PR', 'Pair'),
  unit('RL', 'Reel'),
  unit('RO', 'Roll'),
  unit('SET', 'Set'),
  unit('ST', 'Sheet'),
  unit('TNE', 'Tonne (metric ton)'),
  unit('TU', 'Tube'),
  unit('U', 'Pieces/item'),
  unit('YRD', 'Yard'),
];

export const EBM_PRODUCT_TYPE_OPTIONS = [
  { value: '1', label: 'Raw Material' },
  { value: '2', label: 'Finished Product' },
  { value: '3', label: 'Service' },
  { value: '4', label: 'Assets' },
  { value: '5', label: 'Expenses' },
] as const;

/**
 * Rwanda is the only fiscal integration seeded today
 * (fiscal_integrations.country_iso = 'RW') — this is a default, rarely
 * changed, not a country picker. Kept editable in case a future non-RW
 * fiscal integration is added.
 */
export const EBM_DEFAULT_ORIGIN_NATION_CD = 'RW';
